import { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
} from 'react-leaflet';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import type { Customer } from '@/data/types';
import { useApp } from '@/store';
import { deriveNextInspection } from '@/lib/customer-dates';
import { useT } from '@/lib/i18n';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Fix default icon
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

// Alternative to the reminders list (client 16 July): colour each site by its
// inspection state — overdue red, due-soon amber, otherwise green. Marker hex
// is an allowed exception for Leaflet (see CLAUDE.md).
type MapState = 'overdue' | 'dueSoon' | 'ok' | 'none'; // enum-ignore
const stateColor: Record<MapState, string> = {
  overdue: '#ef4444',
  dueSoon: '#f59e0b',
  ok: '#10b981',
  none: '#94a3b8',
};
const DAY = 86_400_000;
function deviceCount(c: Customer): number {
  return c.extinguishers.length + c.hoses.length;
}
function mapState(c: Customer, now: number, intervalYears: number): MapState {
  if (deviceCount(c) === 0) return 'none';
  const next = deriveNextInspection(c, intervalYears);
  const parts = next?.split('-');
  if (!parts || parts.length !== 3) return 'none';
  const due = Date.UTC(+parts[0], +parts[1] - 1, +parts[2]);
  const days = Math.round((due - now) / DAY);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'dueSoon';
  return 'ok';
}

export function MapPage() {
  const t = useT();
  // The popup links to the customer only when customers are readable (#95).
  const canOpenCustomer = useSectionRights()[SectionId.CUSTOMERS].read;
  const customers = useApp(s => s.customers);
  const interval = useApp(s => s.settings.inspectionInterval);
  const [layer, setLayer] = useState('osm');
  const [show, setShow] = useState({
    overdue: true,
    dueSoon: true,
    ok: true,
    none: true,
    labels: false,
  });
  const [minDevices, setMinDevices] = useState(0);
  const [center] = useState<[number, number]>([47.05, 8.3]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, []);

  const now = Date.UTC(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );
  const colorOf = (c: Customer) => stateColor[mapState(c, now, interval)];
  const visible = customers.filter(
    c =>
      c.lat &&
      c.lng &&
      show[mapState(c, now, interval)] &&
      deviceCount(c) >= minDevices
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold tracking-tight">{t('map.title')}</h1>
        <Card className="flex items-center flex-wrap gap-2 sm:gap-4 p-3">
          <Select value={layer} onValueChange={setLayer}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* i18n-ignore: "OpenStreetMap","Swisstopo" */}
              <SelectItem value="osm">{'OpenStreetMap'}</SelectItem>
              <SelectItem value="swisstopo">{'Swisstopo'}</SelectItem>
            </SelectContent>
          </Select>
          {(['overdue', 'dueSoon', 'ok'] as const).map(k => (
            <div key={k} className="flex items-center gap-2">
              <Checkbox
                id={`m-${k}`}
                checked={show[k]}
                onCheckedChange={v => setShow({ ...show, [k]: !!v })}
              />
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: stateColor[k] }}
                aria-hidden
              />
              <Label htmlFor={`m-${k}`} className="text-xs">
                {t(
                  k === 'overdue'
                    ? 'map.overdue'
                    : k === 'dueSoon'
                      ? 'map.dueSoon'
                      : 'map.upToDate'
                )}
              </Label>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Label htmlFor="m-min" className="text-xs">
              {t('map.minDevices')}
            </Label>
            <Input
              id="m-min"
              type="number"
              min="0"
              value={minDevices}
              onChange={e => setMinDevices(Number(e.target.value) || 0)}
              className="h-8 w-16"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="labels"
              checked={show.labels}
              onCheckedChange={v => setShow({ ...show, labels: !!v })}
            />
            <Label htmlFor="labels" className="text-xs">
              Labels
            </Label>
          </div>
        </Card>
      </div>

      <Card
        className="overflow-hidden p-0"
        style={{ height: 'calc(100dvh - 200px)', minHeight: '300px' }}
        data-tour="map-view"
      >
        <MapContainer
          center={center}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution={
              layer === 'swisstopo' ? '© swisstopo' : '© OpenStreetMap'
            }
            url={
              layer === 'swisstopo'
                ? 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg'
                : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            }
          />
          {visible.map(c => (
            <CircleMarker
              key={c.id}
              center={[c.lat!, c.lng!]}
              radius={10}
              pathOptions={{
                color: colorOf(c),
                fillColor: colorOf(c),
                fillOpacity: 0.8,
              }}
            >
              <Popup>
                <div className="space-y-1">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs">
                    {c.objectAddress?.street}, {c.objectAddress?.city}
                  </div>
                  {canOpenCustomer && (
                    <Link
                      to={`/customer/${c.id}`}
                      className="text-primary text-xs"
                    >
                      Open →
                    </Link>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {visible
            .filter(() => show.labels)
            .map(c => (
              <Marker key={`l-${c.id}`} position={[c.lat!, c.lng!]}>
                <Popup>{c.name}</Popup>
              </Marker>
            ))}
        </MapContainer>
      </Card>
    </motion.div>
  );
}
